import { UploadOutlined } from '@ant-design/icons';
import { Button, type FormItemProps, message, Typography, Upload, type UploadProps } from 'antd';
import type { UploadFile } from 'antd/lib/upload/interface';
import { useField } from 'formik';
import { isString } from 'lodash/fp';
import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { allowedExtensions, formatExtensions, validateExtensions } from '../../utilities/extensions';
import getHash from '../../utilities/getHash';
import FormItem from './FormItem';

export type SingleSecretUploadFileProps = Omit<UploadProps, 'accept' | 'listType' | 'maxCount' | 'defaultFileList'> & {
    name: string;
    label?: string;
    required?: boolean;
    sizeLimitInMiB?: number;
    tooltip?: FormItemProps['tooltip'];
};

const collectFileName = (field: any): string | null => {
    if (field?.value) {
        if (field.name?.includes('privateKey')) {
            return 'Private Key';
        }
        if (field.name?.includes('publicCert')) {
            return 'Public Certificate';
        }
        if (['certificate', 'authority', 'certCA'].some(substring => field.name?.includes(substring))) {
            return 'Certificate';
        }
    }

    return null;
};

const SingleSecretUploadField = ({
    name,
    label,
    required = false,
    sizeLimitInMiB = 2,
    tooltip,
    ...props
}: SingleSecretUploadFileProps) => {
    const { t } = useTranslation(['common']);
    const { Text } = Typography;
    const [field, meta, helpers] = useField(name);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [fileName, setFileName] = useState<string | null>(collectFileName(field));
    const accept = allowedExtensions.keyFile.join(',');
    const formats = formatExtensions(allowedExtensions.keyFile);

    useEffect(() => {
        if (field.value && !(field.value instanceof File) && isString(field.value)) {
            getHash(field.value).then(hashValue => {
                setFileList([
                    {
                        uid: field.value,
                        name: fileName,
                    },
                ]);
            });
        }
    }, [field.value, fileName]);

    const onRemove = useCallback<UploadProps['onRemove']>(() => {
        // manually touch the file
        helpers.setTouched(true);

        // delete the file
        helpers.setValue(null);
        setFileName(null);

        setFileList([]);
    }, [helpers]);

    const beforeUpload = useCallback<UploadProps['beforeUpload']>(
        file => {
            if (file.size > sizeLimitInMiB * 1024 * 1024) {
                message.error(t('common:upload.messages.sizeExceedsLimit', { limit: sizeLimitInMiB }));

                return Upload.LIST_IGNORE;
            }

            const reader = new FileReader();
            const promise = new Promise<boolean>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        setFileName(file.name);
                        helpers.setValue(validationResult.content);

                        return resolve(false);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return Upload.LIST_IGNORE;
                };

                reader.onerror = event => Upload.LIST_IGNORE;
            });

            reader.readAsBinaryString(file);

            return promise;
        },
        [sizeLimitInMiB, t, helpers]
    );

    return (
        <FormItem label={label} meta={meta} name={name} required={required} tooltip={tooltip}>
            <Upload
                accept={accept}
                beforeUpload={beforeUpload}
                disabled={props.disabled}
                fileList={fileList}
                listType="text"
                maxCount={1}
                onRemove={onRemove}
                {...props}
            >
                {!field.value && (
                    <>
                        <Button disabled={props.disabled} icon={<UploadOutlined />}>
                            {t('common:upload.button')}
                        </Button>
                        <br />
                        <Text style={{ fontSize: 10, marginTop: 10 }} type="secondary">
                            {t('common:upload.fileExtensionHint', { formats })}
                        </Text>
                    </>
                )}
            </Upload>
        </FormItem>
    );
};

export default SingleSecretUploadField;
